/**
 * permission-condition — kształt WARUNKU przy regule uprawnień warunkowych (F24).
 *
 * Reguła warunkowa nadaje prawo do rekordu wtedy, gdy dane tego rekordu spełniają warunek —
 * drzewo porównań spięte „i" / „lub". Ten plik opisuje sam kształt warunku; o tym, co z niego
 * wynika, decyduje kompilator po stronie serwera.
 *
 * **Dlaczego nowy typ, skoro w repozytorium są już trzy podobne** (procesy, automatyzacje,
 * warunkowa widoczność pól formatki): tamte są z założenia PRZEPUSZCZAJĄCE. Mają wszystkie pola
 * opcjonalne, a ich ewaluator zwraca „spełniony" dla pustego warunku i dla grupy bez dzieci —
 * bo w procesie niewypełniony warunek ma po prostu nie blokować przepływu. W uprawnieniach ta
 * sama życzliwość znaczy „daj prawo do wszystkiego". Dlatego tutaj obowiązuje zasada odwrotna:
 * **czego nie da się jednoznacznie odczytać, to jest niespełnione**, a niekompletny warunek nie
 * ma prawa powstać — odrzuca go walidator zapisu.
 *
 * Gdyby uprawnienia dziedziczyły typ procesów, każde przyszłe poluzowanie tamtej struktury po
 * cichu poluzowałoby kontrolę dostępu. Kierunek odwrotny jest bezpieczny: procesy mogą kiedyś
 * przyjąć ten typ.
 */

/**
 * Porównania dostępne w warunku.
 *
 * Świadomie NIE ma tu operatora „różne od", który obejmowałby wartości puste: w bazie
 * porównanie z pustą wartością nie daje prawdy, więc `status ≠ szkic` nie złapie rekordu bez
 * statusu. Dla zezwolenia to zachowanie zamykające (rekord po prostu nie wejdzie w zakres
 * reguły), więc je zostawiamy — ale panel musi o nim uprzedzić, bo dla człowieka jest
 * zaskakujące. Kto chce objąć puste, dokłada warunek „nie wypełnione" przez „lub".
 */
export const PERMISSION_CONDITION_OPERATORS = [
  'eq',
  'neq',
  'gt',
  'gte',
  'lt',
  'lte',
  'contains',
  'not_contains',
  'starts_with',
  'in',
  'not_in',
  'is_null',
  'is_not_null',
] as const;

export type PermissionConditionOperator = (typeof PERMISSION_CONDITION_OPERATORS)[number];

/** Operatory, które NIE biorą wartości — panel chowa przy nich pole wartości. */
export const VALUELESS_OPERATORS: ReadonlySet<PermissionConditionOperator> = new Set([
  'is_null',
  'is_not_null',
]);

/** Operatory przyjmujące LISTĘ wartości. */
export const LIST_OPERATORS: ReadonlySet<PermissionConditionOperator> = new Set(['in', 'not_in']);

/** Pojedyncze porównanie: pole, operator, wartość. */
export interface PermissionConditionLeaf {
  kind: 'leaf';
  /**
   * Pole w zapisie `<grupa>.<nazwa>`, np. `self.amount_gross`, `contractor.status`,
   * `process.current_stage`. Dopuszczalne pola podaje serwer — panel ich nie wymyśla.
   */
  field: string;
  operator: PermissionConditionOperator;
  /**
   * Wartość porównania. `null` przy operatorach bez wartości, tablica przy „jedna z" /
   * „żadna z". Podstawienia kontekstu użytkownika zapisuje się jako napis (patrz niżej).
   */
  value: string | number | boolean | string[] | null;
}

/** Grupa spinająca warunki spójnikiem. */
export interface PermissionConditionGroup {
  kind: 'group';
  logic: 'AND' | 'OR';
  children: PermissionCondition[];
}

export type PermissionCondition = PermissionConditionLeaf | PermissionConditionGroup;

/**
 * Podstawienia odnoszące się do osoby pytającej o dostęp — to one czynią regułę warunkową
 * przydatną („moje", „mojego działu"), bo inaczej każdy warunek byłby stały dla wszystkich.
 *
 * Rozwiązuje je serwer w chwili sprawdzania; w regule leży sam znacznik.
 */
export const CONDITION_PLACEHOLDERS = {
  /**
   * Zalogowany użytkownik — osoba, o której dostęp pytamy, NIE ten, kto regułę ustawił. Ten sam
   * znacznik co w filtrach list (`@userId`). Dawny zapis `@me` czyta nadal `isCurrentUserPlaceholder`.
   */
  me: '@userId',
  /** Jednostka organizacyjna zalogowanego użytkownika. */
  myOrgUnit: '@my_org_unit',
  /** Zalogowany użytkownik i wszyscy jego podwładni (przechodnio). */
  mySubordinates: '@my_subordinates',
} as const;

/**
 * Dawny zapis „zalogowanego użytkownika" — reguły zapisane przed zmianą na `@userId` trzymają go
 * w bazie. Nadal działa (bez migracji danych); nowe reguły zapisują `CONDITION_PLACEHOLDERS.me`.
 */
export const LEGACY_CURRENT_USER_PLACEHOLDER = '@me';

export type ConditionPlaceholder =
  | (typeof CONDITION_PLACEHOLDERS)[keyof typeof CONDITION_PLACEHOLDERS]
  | typeof LEGACY_CURRENT_USER_PLACEHOLDER;

const PLACEHOLDER_VALUES: ReadonlySet<string> = new Set([
  ...Object.values(CONDITION_PLACEHOLDERS),
  LEGACY_CURRENT_USER_PLACEHOLDER,
]);

/** Czy wartość jest podstawieniem kontekstu użytkownika. */
export function isConditionPlaceholder(value: unknown): value is ConditionPlaceholder {
  return typeof value === 'string' && PLACEHOLDER_VALUES.has(value);
}

/** Czy wartość to „zalogowany użytkownik" — `@userId` albo dawne `@me`. */
export function isCurrentUserPlaceholder(value: unknown): boolean {
  return value === CONDITION_PLACEHOLDERS.me || value === LEGACY_CURRENT_USER_PLACEHOLDER;
}

// ─── Limity (egzekwowane przy ZAPISIE) ────────────────────────────────────────

/**
 * Limity są twarde i sprawdzane przy zapisie, nie przy odczycie. Odczyt, który „ucina nadmiar",
 * daje wynik zależny od kolejności wierszy — nie do odtworzenia i nie do zaudytowania.
 */
export const CONDITION_LIMITS = {
  /** Zagnieżdżenie grup. Trzy poziomy wystarczają na „A i (B lub (C i D))". */
  maxDepth: 3,
  /** Porównań w jednej regule. */
  maxLeaves: 20,
  /** Włączonych reguł na firmę × kartotekę × prawo. */
  maxEnabledRules: 16,
} as const;

/** Głębokość drzewa: sam liść to 1. */
export function conditionDepth(condition: PermissionCondition): number {
  if (condition.kind === 'leaf') return 1;
  if (condition.children.length === 0) return 1;
  return 1 + Math.max(...condition.children.map(conditionDepth));
}

/** Liczba porównań w drzewie. */
export function countConditionLeaves(condition: PermissionCondition): number {
  if (condition.kind === 'leaf') return 1;
  return condition.children.reduce((sum, child) => sum + countConditionLeaves(child), 0);
}

/** Wszystkie pola użyte w warunku — potrzebne do sprawdzenia, czy zapis ich nie rusza. */
export function collectConditionFields(condition: PermissionCondition): string[] {
  if (condition.kind === 'leaf') return [condition.field];
  return condition.children.flatMap(collectConditionFields);
}

/** Powody, dla których warunek nie nadaje się do zapisania. */
export type ConditionShapeError =
  | 'EMPTY_GROUP'
  | 'TOO_DEEP'
  | 'TOO_MANY_LEAVES'
  | 'UNKNOWN_OPERATOR'
  | 'VALUE_REQUIRED'
  | 'VALUE_NOT_ALLOWED'
  | 'LIST_REQUIRED'
  | 'EMPTY_FIELD';

/**
 * Sprawdza sam kształt warunku — bez zaglądania w to, czy pola istnieją (to wie rejestr źródeł
 * po stronie serwera). Zwraca listę powodów odrzucenia, pustą dla warunku poprawnego.
 *
 * **Pusta grupa jest błędem, nie warunkiem zawsze prawdziwym.** To jest dokładnie ta różnica
 * wobec warunków w procesach, dla której powstał osobny typ.
 */
export function validateConditionShape(condition: PermissionCondition): ConditionShapeError[] {
  const errors = new Set<ConditionShapeError>();

  if (conditionDepth(condition) > CONDITION_LIMITS.maxDepth) errors.add('TOO_DEEP');
  if (countConditionLeaves(condition) > CONDITION_LIMITS.maxLeaves) errors.add('TOO_MANY_LEAVES');

  const walk = (node: PermissionCondition): void => {
    if (node.kind === 'group') {
      if (node.children.length === 0) errors.add('EMPTY_GROUP');
      node.children.forEach(walk);
      return;
    }

    if (node.field.trim() === '') errors.add('EMPTY_FIELD');

    if (!PERMISSION_CONDITION_OPERATORS.includes(node.operator)) {
      errors.add('UNKNOWN_OPERATOR');
      return;
    }

    if (VALUELESS_OPERATORS.has(node.operator)) {
      if (node.value !== null) errors.add('VALUE_NOT_ALLOWED');
      return;
    }

    if (node.value === null) {
      errors.add('VALUE_REQUIRED');
      return;
    }

    if (LIST_OPERATORS.has(node.operator)) {
      if (!Array.isArray(node.value) || node.value.length === 0) errors.add('LIST_REQUIRED');
    } else if (Array.isArray(node.value)) {
      errors.add('VALUE_NOT_ALLOWED');
    }
  };

  walk(condition);
  return [...errors];
}
